/**
 * The guest guide pages' URL shape, as the PHP apex router has it
 * (hub-php _root/index.php:2-20, case-insensitive):
 *
 *   ^(guide|places|tours|gifts|pickup|gift-list|agentportal)(/([a-z0-9-]+))?/?$
 *
 * minus /agentportal, the Agent Portal's redirect (/feedback is not in it and
 * stays on at-mysg.com). /pickup is served here since 2026-10-26. The router
 * trims leading and trailing slashes before matching.
 *
 * /p/<slug> is Amassa's own (Guest Portal ▸ Pages, 2026-10-26): a guest page
 * the tenant wrote. /p/_preview is the editor's preview frame ('p-preview');
 * `_` is never in a slug, so no page can take that address.
 */
export type GuideSection = 'guide' | 'places' | 'tours' | 'gifts' | 'pickup' | 'p' | 'p-preview';

export interface GuidePathMatch {
  section: GuideSection;
  /** As typed — the old-tour-code redirect is case-sensitive. */
  rawSection: string;
  rawDest: string;
  /** Lower-cased, as every page reads it (`strtolower(trim($_GET['dest']))`). */
  dest: string;
}

const GUIDE_PATH = /^\/+(guide|places|tours|gifts|gift-list|pickup|p)(?:\/([a-z0-9-]+))?\/*$/i;
const PREVIEW_PATH = /^\/+p\/_preview\/*$/;

export function matchPublicGuidePath(pathname: string): GuidePathMatch | null {
  if (PREVIEW_PATH.test(pathname)) return { section: 'p-preview', rawSection: 'p', rawDest: '', dest: '' };
  const m = GUIDE_PATH.exec(pathname);
  if (!m) return null;
  const rawSection = m[1] ?? '';
  const lower = rawSection.toLowerCase();
  const rawDest = m[2] ?? '';
  return {
    // gift-list is the router's alias for the gift collection.
    section: (lower === 'gift-list' ? 'gifts' : lower) as GuideSection,
    rawSection,
    rawDest,
    dest: rawDest.toLowerCase(),
  };
}

/** _root/index.php:52 — the old 3-letter tour codes. */
const TOUR_CODE_TO_SLUG: Record<string, string> = {
  KUL: 'kuala-lumpur',
  PEN: 'penang',
  LGK: 'langkawi',
  BKI: 'kota-kinabalu',
  KCH: 'kuching',
  SIN: 'singapore',
};

/**
 * Where PHP sends this path instead of rendering it, or null:
 *   /places, /tours          → 302 …/kuala-lumpur (places:41-44, tours:42-46)
 *   /tours/KUL (upper case)  → 301 /tours/kuala-lumpur (_root/index.php:51-56)
 * Mirrors PublicGuideService (the server answers the same with real HTTP
 * redirects when nginx routes the path to it).
 */
export function guideRedirect(m: GuidePathMatch): string | null {
  if (m.rawSection === 'tours' && /^[A-Z]{2,4}$/.test(m.rawDest)) {
    return `/tours/${TOUR_CODE_TO_SLUG[m.rawDest] ?? m.rawDest.toLowerCase()}`;
  }
  if ((m.section === 'places' || m.section === 'tours') && m.dest === '') {
    return `/${m.section}/kuala-lumpur`;
  }
  return null;
}
